import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import debounce from 'awesome-debounce-promise';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import AuthButton from '../../../components/external/auth-button';
import Required from '../../../components/external/auth-required';
import { parseError } from '../../../services/utils';
import { useService } from '../../../services/services';
import { UserData } from './index';
import authCss from '../auth-forms.module.css';

export default function Step2({
  userData,
  onSubmit,
}: {
  userData: UserData;
  onSubmit: (data: UserData) => void;
}) {
  const { t } = useTranslation();
  const { authService } = useService();
  const [step2Schema] = useState(() =>
    yup.object().shape({
      name: yup.string().max(255).required(),
      farm_name: yup.string().required(),
      farm_registration_number: yup.string().required(),
      email: yup
        .string()
        .max(255)
        .email()
        .required()
        .test(
          'emailUnique',
          'This email is already used for another account!',
          debounce(
            (value) =>
              authService
                .validateEmail(value)
                .pipe(
                  catchError(() => of(false)),
                  map((value) => value !== false)
                )
                .toPromise(),
            1500
          )
        ),
      email_confirmation: yup
        .string()
        .max(255)
        .email()
        .required()
        .oneOf([yup.ref('email')], 'The email confirmation does not match!'),
      phone: yup.string().required(),
      vat: yup.boolean(),
    })
  );

  const {
    register,
    handleSubmit,
    formState,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: userData,
    resolver: yupResolver(step2Schema),
  });
  const { isValid } = formState;

  useEffect(() => {
    reset(userData);
  }, [reset, userData]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <h1>{t('pages.external.register.title')}</h1>
      <p className={authCss['description']}>
        {t('pages.external.register.description')}
      </p>
      <div className={`${authCss['field']} ${authCss['readonly']}`}>
        <label htmlFor="name">
          {t('pages.external.register.name')}
          <Required />
        </label>
        <input
          type="text"
          name="name"
          className={authCss['input']}
          autoComplete="off"
          {...register('name')}
          readOnly={true}
        />
      </div>
      <div className={`${authCss['field']} ${authCss['readonly']}`}>
        <label htmlFor="farm_name">
          {t('pages.external.register.farm-name')}
          <Required />
        </label>
        <input
          type="text"
          name="farm_name"
          className={authCss['input']}
          autoComplete="off"
          {...register('farm_name')}
          readOnly
        />
      </div>
      <div className={`${authCss['field']} ${authCss['readonly']}`}>
        <label htmlFor="farm_register_number">
          {t('pages.external.register.farm-registration-number')}
          <Required />
        </label>
        <input
          type="text"
          name="farm_registration_number"
          className={authCss['input']}
          autoComplete="off"
          {...register('farm_registration_number')}
          readOnly
        />
      </div>
      <div
        className={`${authCss['field']} ${
          errors.email ? authCss['has-error'] : ''
        }`}
      >
        <label htmlFor="email">
          {t('pages.external.register.email')}
          <Required />
        </label>
        <input
          type="text"
          name="email"
          className={authCss['input']}
          autoComplete="off"
          readOnly={userData['email'] && userData['user_verified']}
          {...register('email')}
        />
        {errors.email && (
          <span className={authCss['error']}>
            {t(parseError('email', errors)?.message)}
          </span>
        )}
      </div>
      <div
        className={`${authCss['field']} ${
          errors.email_confirmation ? authCss['has-error'] : ''
        }`}
      >
        <label htmlFor="email_confirmation">
          {t('pages.external.register.email-repeat')}
          <Required />
        </label>
        <input
          type="text"
          name="email_confirmation"
          className={authCss['input']}
          autoComplete="off"
          readOnly={userData['email'] && userData['user_verified']}
          {...register('email_confirmation')}
        />
        {errors.email_confirmation && (
          <span className={authCss['error']}>
            {t(parseError('email_confirmation', errors)?.message)}
          </span>
        )}
      </div>
      <div
        className={`${authCss['field']} ${
          errors.phone ? authCss['has-error'] : ''
        }`}
      >
        <label htmlFor="phone">
          {t('pages.external.register.phone')}
          <Required />
        </label>
        <input
          type="text"
          name="phone"
          className={authCss['input']}
          autoComplete="off"
          {...register('phone')}
        />
        {errors.phone && (
          <span className={authCss['error']}>
            {t(parseError('phone', errors)?.message)}
          </span>
        )}
      </div>
      <div className={authCss['checkbox-field']}>
        <input
          type="checkbox"
          name="vat"
          id="vat"
          className={authCss['checkbox']}
          {...register('vat')}
        />
        <label htmlFor="vat">{t('pages.external.register.vat')}</label>
      </div>
      <div className={authCss['buttons']}>
        <AuthButton
          name="next"
          label={t('pages.external.register.next')}
          type="submit"
          disabled={!isValid}
        />
      </div>
    </form>
  );
}
